import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import GlobalSnackbar from "../../../shared/utils/GlobalSnackbar.jsx";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import PDF from "../../../../src/shared/assets/icons/PDF.png";
import JPG from "../../../../src/shared/assets/icons/JPG.png";
import XLS from "../../../../src/shared/assets/icons/XLS.png";
import DOC from "../../../../src/shared/assets/icons/DOC.png";
import PNG from "../../../../src/shared/assets/icons/PNG.png";
import style from "../../../../src/index.css";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Menu,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  IconButton,
  Autocomplete,
  TextField,
  Modal,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Swal from "sweetalert2";
import Pagination from "@mui/material/Pagination";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Settings from "@mui/icons-material/Settings";
import styles from "../../../styles/adminPortal.module.css";
import Styles from "../../../styles/vendorDashboard.module.css";
import AddIcon from "@mui/icons-material/Add";
import EnquiryModal from "../../../shared/createForms/EnquiryModal";
import KamDropdown from "../Campaign/RFPModal";
import CustomLoading from "../../../shared/utils/CustomLoading";
import SowModal from "../../../shared/createForms/SowModal";
import SowViewModal from "../../../shared/createForms/SowViewModal";
import ClientMembersModal from "../../../shared/createForms/ClientMembersModal";
import ReportsMainModal from "../../../shared/createForms/ReportsMainModal";
import SowReportDialog from "../../../shared/createForms/SowReportModal";
import User_plus from "../../../shared/assets/icons/User_plus.jsx";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadExcelForInputLocations } from "./downloadExcelSheetForInputLocations.jsx";
import { PRODUCT_TYPES } from "../../../shared/enums/productTypeList.js";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { handleFileUploadForLocations } from "./uploadExcelSheetForUploadingLocations.jsx";
import GlobalDropdownField from "../../../shared/utils/GlobalDropdownField.jsx";
import DeletedUsersRestore from "../../../shared/createForms/DeletedUsersRestore.jsx";
import GlobalButton from "../../../shared/utils/GlobalButton";

const ServicesSettingsDialog = ({
  open,
  onClose,
  services,
  rfpId,
  rfpSerialNumber,
  onServiceToggle,
  isOldRFP = false,
  setSnackbar,
  missingServicesCount = 0,
  totalServicesCount = 0,
}) => {
  const [localServices, setLocalServices] = useState([]);

  const [loading, setLoading] = useState(false);
  const [currentMissingCount, setCurrentMissingCount] =
    useState(missingServicesCount);

  // Simplified normalization
  useEffect(() => {
    if (!Array.isArray(services)) {
      setLocalServices([]);
      return;
    }

    const normalizedServices = services.map((service) => {
      return {
        id: service._id,
        displayName: getServiceDisplayName(service),
        isActive: Boolean(service.enabled ?? service.isActive),
        isRegionalActive: service?.regionalBased,
        originalData: service,
      };
    });

    setLocalServices(normalizedServices);
  }, [services]);

  // Update missing count when props change
  useEffect(() => {
    setCurrentMissingCount(missingServicesCount);
  }, [missingServicesCount]);

  // Extract Details
  const getServiceDisplayName = (service) => {
    const nameSources = [
      service.serviceName,
      service.serviceId?.serviceName,
      service.service?.serviceName,
    ];
    return nameSources.find((name) => name) || "Unnamed Service";
  };

  const extractServiceId = (service) => {
    // finding service ID
    if (service.serviceId && typeof service.serviceId === "object") {
      return service.serviceId._id;
    }
    if (service.serviceId) {
      return service.serviceId;
    }
    if (service.service && service.service._id) {
      return service.service._id;
    }
    return service._id;
  };

  const isValidObjectId = (id) => {
    return typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);
  };

  const handleToggle = (serviceId) => {
    setLocalServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? { ...service, isActive: !service.isActive }
          : service,
      ),
    );
  };

  const handleToggleRegional = (serviceId) => {
    setLocalServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? { ...service, isRegionalActive: !service.isRegionalActive }
          : service,
      ),
    );
  };
  // Function to fix old RFP services
  const handleFixOldRFP = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/service-schedules/rfp/${rfpId}/fix`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        // Reload services
        const updatedResponse = await axios.get(
          `${process.env.REACT_APP_URL}/service-schedules/rfp/${rfpId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (updatedResponse.data.success) {
          const updatedServices = updatedResponse.data.data.map((service) => ({
            id: service._id,
            displayName: getServiceDisplayName(service),
            isActive: Boolean(service.enabled),
            isRegionalActive: service?.originalData?.regionalBased,
            originalData: service,
          }));

          setLocalServices(updatedServices);
          setCurrentMissingCount(0);
        }

        if (setSnackbar) {
          setSnackbar({
            open: true,
            message:
              response.data.message ||
              "Services added successfully! All services are disabled by default.",
            severity: "success",
          });
        }
      }
    } catch (error) {
      console.error("Error fixing old RFP:", error);
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: "Failed to add services",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // API call logic
  const updateServiceSchedule = async (
    serviceId,
    enabled,
    isRegionalActive,
  ) => {
    const token = localStorage.getItem("token");
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const response = await axios.put(
      `${process.env.REACT_APP_URL}/service-schedules`,
      { rfpId, serviceId, enabled, isRegionalActive },
      headers,
    );

    return response.data;
  };

  // 5. Main save handler
  const handleSave = async () => {
    try {
      const updates = localServices.map((service) => ({
        serviceId: extractServiceId(service.originalData),
        enabled: service.isActive,
        localService: service,
        isRegionalActive: service.isRegionalActive,
      }));

      // Filter valid updates
      const validUpdates = updates.filter((update) =>
        isValidObjectId(update.serviceId),
      );

      const invalidUpdates = updates.filter(
        (update) => !isValidObjectId(update.serviceId),
      );
      if (invalidUpdates.length > 0) {
        console.warn("Skipping invalid service entries:", invalidUpdates);
      }

      // Execute all updates
      const updatePromises = validUpdates.map((update) =>
        updateServiceSchedule(
          update.serviceId,
          update.enabled,
          update.isRegionalActive,
        ),
      );

      const results = await Promise.allSettled(updatePromises);

      // Process results
      const successfulUpdates = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value.data);

      // Check if any updates failed
      const failedCount = results.filter(
        (result) => result.status === "rejected",
      ).length;

      if (failedCount > 0) {
        console.warn(`${failedCount} service updates failed`);
      }

      // Notify parent component
      if (onServiceToggle) {
        const updatedServices =
          successfulUpdates.length > 0
            ? successfulUpdates
            : localServices.map((service) => ({
                ...service.originalData,
                enabled: service.isActive,
              }));

        onServiceToggle(updatedServices);
      }

      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: "Service settings updated successfully",
          severity: "success",
        });
      }

      onClose();
    } catch (error) {
      console.error("Failed to save service settings:", error);
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: "Failed to save service settings",
          severity: "error",
        });
      }
    }
  };

  const shouldShowFixButton =
    localServices.length === 0 || currentMissingCount > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" component="span">
              Services Settings
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              Enquiry: {rfpSerialNumber}
              {currentMissingCount > 0 && (
                <Chip
                  label={`${currentMissingCount} missing`}
                  size="small"
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
              {isOldRFP && localServices.length === 0 && (
                <Chip
                  label="Old RFP"
                  size="small"
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {shouldShowFixButton ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" gutterBottom color="textSecondary">
              {localServices.length === 0
                ? "This RFP has no services configured."
                : `${currentMissingCount} services are missing.`}
            </Typography>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {localServices.length === 0
                ? `Click below to add all ${totalServicesCount} services (disabled by default).`
                : `This RFP is missing ${currentMissingCount} out of ${totalServicesCount} services.`}
            </Typography>

            <GlobalButton
              onClick={handleFixOldRFP}
              disabled={loading}
              isLoading={loading}
            >
              {localServices.length === 0
                ? `Add All ${totalServicesCount} Services`
                : `Add ${currentMissingCount} Missing Services`}
            </GlobalButton>
          </Box>
        ) : (
          <List>
            {localServices.length === 0 ? (
              <ListItem>
                <ListItemText primary="No services available" />
              </ListItem>
            ) : (
              localServices.map((service) => (
                <ServiceListItem
                  key={service.id}
                  service={service}
                  onToggle={handleToggle}
                  onToggleRegional={handleToggleRegional}
                />
              ))
            )}
          </List>
        )}
      </DialogContent>

      {localServices.length > 0 && currentMissingCount === 0 && (
        <DialogActions>
          <GlobalButton
            variant="primary"
            size="medium"
            onClick={handleSave}
          >{`Save Changes`}</GlobalButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

const SERVICE_LABELS = {
  congratulationMessageToShopOwner: "Congratulation Message To the Shop Owner",
  campaignStartingMessageToTheSO: "Campaign Started Message to All SO",
  orderConfirmationMail: "Order Confirmation Mail",
  rejectedMessageToTheSO: "Job Rejection Message to the SO",
};
//  Extract list item component
const ServiceListItem = ({ service, onToggle, onToggleRegional }) => {
  return (
    <ListItem divider sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <ListItemText
        primary={SERVICE_LABELS[service.displayName] || service.displayName}
        secondary={`Status: ${service.isActive ? "Active" : "Inactive"}`}
      />

      {/* Regional Toggle */}
      {service.isActive && service.displayName != "orderConfirmationMail" && (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2">Regional</Typography>
          <Switch
            checked={service?.isRegionalActive}
            onChange={() => onToggleRegional(service.id)}
            color="secondary"
          />
        </Box>
      )}

      {/* Active Toggle */}
      <Box display="flex" alignItems="center" gap={1}>
        {/* <Typography variant="body2">Active</Typography> */}
        <Switch
          checked={service.isActive}
          onChange={() => onToggle(service.id)}
          color="primary"
        />
      </Box>
    </ListItem>
  );
};

const getHeadCells = (role) => [
  { id: "rfpSerialNumber", label: "Enquiry Id", width: 100 },
  { id: "organizationName", label: "Client Name", width: 200 },
  { id: "campaign", label: "Campaign", width: 160 },
  { id: "owner", label: "Client POC", width: 150 },
  { id: "turnAroundTime", label: "Project TAT", width: 150 },
  { id: "productType", label: "Product Type", width: 150 },
  { id: "associatedBrands", label: "Associated Brands", width: 180 },

  ...(role === "-1" || role === "1"
    ? [{ id: "assign", label: "Assign CSMs", width: 170 }]
    : []),

  ...(role === "3"
    ? []
    : [
        ...(role === "0"
          ? [] // ❌ role 0 → no Order Status
          : [{ id: "status", label: "Order Status", width: 180 }]),

        { id: "action", label: "Action", width: 100 },

        ...(role === "0"
          ? [] // ❌ role 0 → no Services
          : [{ id: "services", label: "Services Settings", width: 140 }]),
      ]),
];

function EnhancedTableHead(props) {
  const [headCells, setHeadCells] = useState([]);

  useEffect(() => {
    const role = localStorage.getItem("role");
    setHeadCells(getHeadCells(role));
  }, []);
  return (
    <TableHead className={styles.tableHead}>
      <TableRow>
        {headCells.map((headCell, index) => (
          <TableCell
            key={headCell.id}
            className={styles.tableCell}
            sx={{
              minWidth: headCell.width,
            }}
          >
            <span className={styles.headCellHeading}> {headCell.label} </span>

            <div className={styles.topBorderDiv}>
              {["campaign", "owner"].includes(headCell.id) && (
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={`Search`}
                  onChange={(e) => props.handleSearchChangeHead(e, headCell.id)}
                  value={props.searchInputs?.[headCell.id] || ""} // Access search inputs from props
                />
              )}
              {["status"].includes(headCell.id) && (
                <GlobalDropdownField
                  name="machinesFilter"
                  variant="searchDropdown"
                  value={
                    props.searchInputs?.[headCell.id]
                      ? props.searchInputs[headCell.id]
                          .charAt(0)
                          .toUpperCase() +
                        props.searchInputs[headCell.id].slice(1)
                      : ""
                  }
                  onChange={(e) => props.handleSearchChangeHead(e, headCell.id)}
                  placeholder="Select Machine type"
                  options={[
                    { value: "", label: "Both" },
                    { value: "Order Confirmed", label: "Order Confirmed" },
                    {
                      value: "Confirmation Pending",
                      label: "Confirmation Pending",
                    },
                  ]}
                  renderValueFn={(selected) => {
                    if (selected) return selected;
                    return "All";
                  }}
                />
              )}
            </div>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar(props) {
  const userRole = localStorage.getItem("role");
  const { numSelected } = props;
  const handleOpen = (e) => {
    props.setSelectedRFP(null);
    props.setRfpGenerate(true);
    props.setOperation("");
  };
  const handleTrashOpen = async (e) => {
    props.getALLDeletedEnquiries();
    props.OnTrashModalOpen(true);
  };
  return (
    <>
      <div
        // className="mb-2 shadow-lg"
        className=" border-b flex justify-between items-center h-14 pt-2"
      >
        <div className="font-bold text-md text-gray-600">
          Total Enquiry : {props.totalRfp}
        </div>
        <div className="flex gap-1 justify-center items-center">
          {(userRole === "-1" || userRole == "1") && (
            <>
              <GlobalButton
                variant="secondary"
                size="small"
                leftIcon={<AddIcon />}
                onClick={handleOpen}
              >
                {`Add Enquiry`}
              </GlobalButton>
            </>
          )}
          {userRole === "-1" && (
            <>
              <Tooltip title="Trash">
                <IconButton onClick={handleTrashOpen}>
                  <DeleteOutlineIcon sx={{ color: "#666666" }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function EnhancedTable() {
  const [pageSize, setPageSize] = useState(20);
  const pageSizeOptions = [1, 5, 10, 20, 50, 100];
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const serverURL = process.env.REACT_APP_URL;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [kamdata, setKamdata] = useState([]);
  const [openKamAssignModal, setOpenKamAssignModal] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState({}); // State for selected value
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isSowModal, setIsSowModal] = useState(false);
  const [isViewModal, setIsViewModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(false);
  const [rfpListLoading, setRfpListLoading] = useState(true);
  const [totalRfp, setTotalRfp] = useState("0");
  const [headCells, setHeadCells] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isReportsModal, setReportsModal] = useState(false);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();
  const [clientAddIndicationId, setClientAddIndicationId] = useState(null);
  const [servicesDialogOpen, setServicesDialogOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedRfpForServices, setSelectedRfpForServices] = useState(null);
  const [selectedRfpSerialNumber, setSelectedRfpSerialNumber] = useState(null);
  const [servicesDialogData, setServicesDialogData] = useState({
    services: [],
    rfpId: null,
    rfpSerialNumber: null,
    isOldRFP: false,
    missingServicesCount: 0,
    totalServicesCount: 0,
  });

  useEffect(() => {
    const role = localStorage.getItem("role");
    setHeadCells(getHeadCells(role));
  }, []);
  const [searchInputs, setSearchInputs] = useState({
    state: "",
    city: "",
    phoneNumber: "",
    name: "",
  });
  const [rfpmodalOpen, setRfpModalOpen] = useState(false);
  const [adminPortalView, setAdminPortalView] = useState(true);
  const [rfpGenerate, setRfpGenerate] = useState(false);
  const [selectedRfpId, setSelectedRfpId] = useState(null);
  const [selectedRfpKamId, setSelectedRfpKamId] = useState(null);
  const [allRfp, setAllRfp] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [rfpIds, setRfpIds] = useState([]);
  const [isEnquiryData, setEnquiryData] = useState(null);
  const [SOWUpdateDetails, setSOWUpdateDetails] = useState([]);
  const [locationListUpdate, setLocationListUpdate] = useState([]);
  const [sowIds, setSowIds] = useState([]);
  const [isProductDimensions, setProductDimension] = useState([]);
  const [isCampaignId, setIsCampaignId] = useState("");
  const [isSowReportOpen, setIsSowReportOpen] = useState(false);
  const [isRFPModal, setIsRFPModal] = useState(false);
  const [isSelectedRFP, setSelectedRFP] = useState({});
  const [operation, setOperation] = useState("");
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [isOrganizationId, setIsOrganizationId] = useState(null);
  const sowReportRef = useRef(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const locationFileInputRef = useRef(null);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]); // selected checkboxes
  const [isTrashModalOpen, setIsTrashModalOpen] = useState(false);
  const [allDeletedEnquiries, setAllDeletedEnquiries] = useState([]);
  const [isTrashLoading, setTrashLoading] = useState(false);

  const [selectedRowId, setSelectedRowId] = useState(null);
  let debounceTimeout = null;

  // Handle opening services settings dialog
  const handleOpenServicesDialog = async (rfp) => {
    try {
      const token = localStorage.getItem("token");

      // Fetch fresh services data from API
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/service-schedules/rfp/${rfp._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        // Store all the data including missingServicesCount
        setSelectedServices(response.data.data || []);
        setSelectedRfpForServices(rfp._id);
        setSelectedRfpSerialNumber(rfp.rfpSerialNumber || null);

        // Store the API response data for the dialog
        setServicesDialogData({
          services: response.data.data || [],
          rfpId: rfp._id,
          rfpSerialNumber: rfp.rfpSerialNumber || null,
          isOldRFP: response.data.isOldRFP || false,
          missingServicesCount: response.data.missingServicesCount || 0,
          totalServicesCount: response.data.totalServicesCount || 0,
        });

        setServicesDialogOpen(true);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      // Fallback to existing data if API fails
      setSelectedServices(rfp.serviceSchedules || []);
      setSelectedRfpForServices(rfp._id);
      setSelectedRfpSerialNumber(rfp.rfpSerialNumber || null);

      // Set default values
      setServicesDialogData({
        services: rfp.serviceSchedules || [],
        rfpId: rfp._id,
        rfpSerialNumber: rfp.rfpSerialNumber || null,
        isOldRFP: rfp.serviceSchedules?.length === 0 || false,
        missingServicesCount: 0,
        totalServicesCount: 0,
      });

      setServicesDialogOpen(true);
    }
  };

  // Handle closing services settings dialog
  const handleCloseServicesDialog = () => {
    setServicesDialogOpen(false);
    setSelectedServices([]);
    setSelectedRfpForServices(null);
    setSelectedRfpSerialNumber(null);
    setServicesDialogData({
      services: [],
      rfpId: null,
      rfpSerialNumber: null,
      isOldRFP: false,
      missingServicesCount: 0,
      totalServicesCount: 0,
    });
  };

  // Handle service toggle updates
  const handleServiceToggle = (updatedServices) => {
    setAllRfp((prevRfps) =>
      prevRfps.map((rfp) =>
        rfp._id === selectedRfpForServices
          ? { ...rfp, serviceSchedules: updatedServices }
          : rfp,
      ),
    );

    setSnackbar({
      open: true,
      message: "Services updated successfully",
      severity: "success",
    });
  };

  const getSOWIds = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/product/scopeOfWorks/ids`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success === true) {
        setSowIds(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenKamModal = (e, v) => {
    setSelectedRfpId(v._id);
    setSelectedRfpKamId(v.keyAccountManagerId); // Set the current KAM ID (if any)
    setRfpModalOpen(true); // Open the modal
  };
  const handleCloseKamModal = () => setRfpModalOpen(false);

  const getRfpList = async (page = 1, limit = 20) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/requestForProposal/viewAllRfp?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success === true) {
        setAllRfp(response.data.data);
        setRfpIds(response.data.data.map((item) => item._id));

        setKamdata(response?.data?.adminsData);
        setTotalRfp(response.data.totalRfp);
        setTotalPages(response?.data?.totalPages);
        setRfpListLoading(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getRfpList(page, pageSize);
  }, []);

  useEffect(() => {
    getSOWIds();
  }, []);

  const handleModalOpen = (value) => {
    setIsModalOpen(value);
  };
  const handleSowReportOpen = (sowId) => {
    setSelectedRow(sowId);
    setIsSowReportOpen(true);
  };

  const handleSowReportClose = () => setIsSowReportOpen(false);

  const handlePageChange = async (e, value) => {
    setPage(value); // Update the page state

    const queryParams = new URLSearchParams();

    Object.entries(searchInputs).forEach(([key, searchValue]) => {
      if (searchValue.trim() !== "") {
        queryParams.append(key, searchValue);
      }
    });

    queryParams.append("page", value);
    queryParams.append("limit", pageSize); // Assuming pageSize is the number of items per page

    // Call the API with the query params
    await fetchRFPs(queryParams.toString());
  };

  const handlePageSizeChange = async (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);

    const queryParams = new URLSearchParams();

    Object.entries(searchInputs).forEach(([key, searchValue]) => {
      if (searchValue.trim() !== "") {
        queryParams.append(key, searchValue);
      }
    });

    // Add the new page and page size to the query params
    queryParams.append("page", 1);
    queryParams.append("limit", newSize);
    await fetchRFPs(queryParams.toString());
  };
  const getALLDeletedEnquiries = async () => {
    try {
      setTrashLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/requestForProposal/allDeletedEnquiries`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response?.data?.success) {
        setAllDeletedEnquiries(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching InstallationPartner:", error);
    } finally {
      setTrashLoading(false);
    }
  };

  const fetchRFPs = async (queryParams) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/requestForProposal/viewAllRfp?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response?.data?.success) {
        setAllRfp(response.data.data);
        setTotalPages(response?.data?.totalPages);
        setPage(response?.data?.currentPage);
      }
    } catch (error) {
      console.error("Error fetching filtered vendors:", error);
    } finally {
    }
  };
  const handleSearchChangeHead = async (e, key) => {
    const value = e.target.value.toLowerCase();

    setSearchInputs((prevState) => {
      const updatedInputs = { ...prevState, [key]: value };

      // Check if all search fields are empty, reset to full API fetch
      const allFieldsEmpty = Object.values(updatedInputs).every(
        (val) => val.trim() === "",
      );

      if (allFieldsEmpty) {
        getRfpList(1, pageSize);
        return updatedInputs;
      }

      // Clear previous timeout before setting a new one
      if (debounceTimeout) clearTimeout(debounceTimeout);

      // Set a new timeout of 3 seconds before sending the API request
      // debounceTimeout = setTimeout(() => {
      // Construct API Query Parameters based on filled fields
      let queryParams = new URLSearchParams();

      Object.entries(updatedInputs).forEach(([searchKey, searchValue]) => {
        if (searchValue.trim() !== "") {
          queryParams.append(searchKey, searchValue);
        }
      });

      // Add pagination info (page and limit) to the queryParams
      queryParams.append("page", 1); // Current page
      queryParams.append("limit", pageSize); // Number of rows per page

      // Call API with filtered search parameters after 3 seconds
      fetchRFPs(queryParams.toString());
      // }, 1000); // Delay of 3000ms (3 seconds)

      return updatedInputs;
    });
  };

  function formatLocation(location) {
    const firstTwoStates = location.slice(0, 2).join(", ");
    const remainingStatesCount =
      location.length > 2 ? ` +${location.length - 2}` : "";
    return firstTwoStates + remainingStatesCount;
  }
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0"); // Ensures two-digit day
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensures two-digit month (Months are 0-based)
    const year = date.getFullYear(); // Full four-digit year

    return `${day}/${month}/${year}`;
  };

  const getKAMNamesForTooltip = (ids) => {
    // Map through KAM IDs and return the corresponding names
    const kamNames = ids.map((id) => {
      const kam = kamdata.find((kam) => kam._id === id);
      return kam ? kam.name : "No KAM Assigned";
    });
    // Return the full list of names
    return kamNames.join(", ");
  };

  const getKAMName = (ids) => {
    // If ids is an array, map through it and find the corresponding KAM names
    const kamNames = ids
      .map((id) => {
        const kam = kamdata.find((kam) => kam._id === id);
        return kam ? kam.name : "No KAM Assigned";
      })
      .slice(0, 2); // Only take the first two names

    // If there are more than 2 KAMs, add the '+X' for extra KAMs
    const extraKAMCount = ids.length - 2;

    // Combine the names with a comma and add '+X' if there are more than 2 KAMs
    return (
      kamNames.join(", ") + (extraKAMCount > 0 ? ` +${extraKAMCount}` : "")
    );
  };

  const createCampaignFromRfp = async (rfpData) => {
    try {
      const response = await axios.post(
        `${serverURL}/requestForProposal/createCampaign`, // Use the correct endpoint
        { rfpData, selectedMembers, enquiryStatus: status }, // Send as { rfpData: {...} } if that's what backend expects
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (response?.data?.success) {
        Swal.fire({
          customClass: { container: "my-swal" },
          title: "Success!",
          text: "Order Confirmed and Email sent successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          setSnackbar({
            open: true,
            message: "Campaign created successfully",
            severity: "success",
          });
          getRfpList();
        });
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
      setSnackbar({
        open: true,
        message: "Failed to create campaign",
        severity: "error",
      });
    }
  };

  const handleStatusChange = async (e, v, confirmedStatus = null) => {
    const isChecked = e.target.checked;

    // 👇 Convert toggle → status string
    const selectedStatus =
      confirmedStatus ||
      (isChecked ? "Order Confirmed" : "Confirmation Pending");

    const originalStatus = v.status;

    // Only show confirmation popup when turning ON
    if (selectedStatus === "Order Confirmed" && !confirmedStatus) {
      e.preventDefault();

      processStatusChange(e, v, selectedStatus);
      return;
    }

    // Normal flow
    await processStatusChange(e, v, selectedStatus);
  };
  const processStatusChange = async (e, v, selectedStatus) => {
    setFormData(v);
    setStatus(selectedStatus); // Update local state
    const hasCSM = v?.keyAccountManagerId?.some((id) => {
      const member = kamdata.find((ele) => ele._id === id);
      return member?.role === 0;
    });

    if (!hasCSM) {
      setClientAddIndicationId(v._id);

      setSnackbar({
        open: true,
        message: "Assign at least one CSM in this Enquiry",
        severity: "error",
      });

      return;
    }

    if (!v.locationsUploaded) {
      return setSnackbar({
        open: true,
        message: "Locations not uploaded",
        severity: "error",
      });
    }
    const hasTotalPriceInAll = v.products.every(
      (p) =>
        (p?.costCalculator?.totalCostOfProduct !== "" &&
          p?.costCalculator?.totalCostOfProduct > 0) ||
        p?.costCalculator?.regionWiseCosts.every(
          (r) => r?.totalCostOfProduct > 0,
        ),
    );

    if (!hasTotalPriceInAll) {
      return setSnackbar({
        open: true,
        message: "Cost of some products are missing",
        severity: "error",
      });
    }

    // loader start for generating reports and save to backend also to the enquiry
    // setReportsModal(true);

    // this modal is for select client member to get order confirmation mail
    setOpenKamAssignModal(true);
    setIsOrganizationId(v?.organizationId?._id);
    setEnquiryData(v);
  };

  const handleViewRFP = async (v, operation) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/requestForProposal/viewRfp/${v?._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.data.success === true) {
        setSelectedRFP(response.data.data);
        setOperation(operation);
        setRfpGenerate(true);
        setAnchorEl(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDuplicateRFP = async (v, operation) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/requestForProposal/viewRfp/${v?._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.data.success === true) {
        const {
          locationsUploaded,
          locationsUploadedUpdatedAt,
          ...filteredData
        } = response.data.data;
        setSelectedRFP(filteredData);
        setOperation(operation);
        setRfpGenerate(true);
        setAnchorEl(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const handleIconClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null); // Reset the selected row ID
  };
  const handleSowClick = async (e, v) => {
    // e.preventDefault();
    setIsSowModal(true);
  };
  const handleSowViewClick = async (e, v) => {
    // e.preventDefault();
    setIsViewModal(true);
  };
  const handleIconOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId); // Set the selected row ID
  };

  const handleSaveRfp = async (e) => {
    e.preventDefault();
  };

  const handleReports = async (e, v) => {
    setFormData(v);
    setReportsModal(true);
  };
  const handleDelete = async (e, v) => {
    setAnchorEl(null);
    Swal.fire({
      customClass: {
        container: "my-swal",
      },
      icon: "warning",
      text: "Do you want to delete this Enquiry?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(
            `${serverURL}/requestForProposal/deleteRfp/${v._id}`,

            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );

          if (response.data.success === true) {
            // Show success message
            Swal.fire({
              customClass: {
                container: "my-swal",
              },
              icon: "success",
              text: response.data.message,
              padding: "1em",
            }).then(() => {
              getRfpList();
            });
          }
        } catch (error) {
          console.error("Error deleting campaign:", error);

          // Show error message if delete fails
          Swal.fire({
            customClass: {
              container: "my-swal",
            },
            icon: "error",
            text: error.response?.data?.message || "An error occurred",
            color: "black",
          });
        }
      }
    });
  };

  const handleUploadLocations = async () => {
    if (!selectedFile) return;

    // 🔁 fake event to match existing function
    const fakeEvent = {
      target: {
        files: [selectedFile],
      },
    };

    const response = await handleFileUploadForLocations(
      fakeEvent,
      setExcelLoading,
      setSnackbar,
      true, // saveExcel
      productTypes,
    );

    if (!response?.success) {
      return setSnackbar({
        open: true,
        message: response?.message || "Failed to upload locations",
        severity: "error",
      });
    }

    const locationString = response.data;

    try {
      const res = await axios.put(
        `${serverURL}/requestForProposal/editRfpLocation/${selectedRow?._id}`,
        {
          locationsUploaded: locationString,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (res.data.success) {
        getRfpList(); // Refresh the RFP list to show updated locations
      }
    } catch (error) {
      console.error("Error updating locations:", error);
      return setSnackbar({
        open: true,
        message: "Failed to update locations",
        severity: "error",
      });
    }

    setSnackbar({
      open: true,
      message: "Locations uploaded successfully",
      severity: "success",
    });

    setOpenUploadModal(false);
    setSelectedFile(null);
    setSelectedRowId(null);
  };

  const handleIconClick = () => {
    locationFileInputRef.current?.click();
  };

  return (
    <div className={styles.tableGround}>
      <Box>
        {!rfpListLoading ? (
          <>
            <Paper
              sx={{ width: "100%", boxShadow: "none" }}
              className={styles.tableGap}
            >
              <EnhancedTableToolbar
                userVenderData={getRfpList}
                OnModalOpen={(value) => handleModalOpen(value)}
                onSearchChange={(value) => handleSearchChangeHead(value)}
                totalRfp={totalRfp}
                setRfpGenerate={setRfpGenerate}
                setSelectedRFP={setSelectedRFP}
                setOperation={setOperation}
                OnTrashModalOpen={setIsTrashModalOpen}
                getALLDeletedEnquiries={getALLDeletedEnquiries}
              />

              {rfpGenerate && (
                <EnquiryModal
                  adminPortalView={adminPortalView}
                  setRfpGenerate={setRfpGenerate}
                  getRfpList={getRfpList}
                  onSubmit={handleSaveRfp}
                  rfpGenerate={rfpGenerate}
                  selectedCampaign={isSelectedRFP}
                  operation={operation}
                />
              )}

              <>
                <TableContainer
                  className={`${styles.tableContainer} ${Styles["custom-scrollbar"]}`}
                >
                  <Table
                    stickyHeader
                    // sx={{ minWidth: 750 }}
                    className="rounded-lg "
                  >
                    <EnhancedTableHead
                      userListData={getRfpList}
                      setSearchInputs={setSearchInputs} // Function to update search inputs
                      handleSearchChangeHead={handleSearchChangeHead}
                      searchInputs={searchInputs}
                    />

                    {allRfp && allRfp?.length > 0 ? (
                      <TableBody>
                        {allRfp.map((v, index) => {
                          const allowedServices = [
                            "Printing",
                            "Fabrication",
                            "Painting",
                          ];

                          const hasReportService = v?.products?.some(
                            (product) =>
                              product?.servicesNeeded?.some((service) =>
                                allowedServices.includes(service),
                              ),
                          );
                          return (
                            <TableRow
                              hover
                              className={styles.tableRowHead}
                              tabIndex={-1}
                              onClick={() => {
                                handleViewRFP(v, "view");
                                // handleViewClick(e, v._id, "read");

                                setSelectedRow(v);
                              }}
                              key={index}
                            >
                              <TableCell
                                sx={{ maxWidth: "200px" }}
                                className={styles.tableRow}
                              >
                                <Tooltip
                                  title={v?.rfpSerialNumber}
                                  arrow
                                  placement="top"
                                >
                                  <span className={styles.truncateText}>
                                    {v?.rfpSerialNumber || "Not Available"}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              {/* <TableCell
                                sx={{ maxWidth: "150px" }}
                                className={styles.tableRow}
                              >
                                <Tooltip
                                  title={
                                    !v?.quotationId && !v?.totalCost
                                      ? "NA"
                                      : `${v?.quotationId || "NA"} : ${
                                          v?.totalCost ?? "NA"
                                        }`
                                  }
                                >
                                  <span className={styles.truncateText}>
                                    {!v?.quotationId && !v?.totalCost
                                      ? "NA"
                                      : `${v?.quotationId || "NA"} : ${
                                          v?.totalCost ?? "NA"
                                        }`}
                                  </span>
                                </Tooltip>
                              </TableCell> */}
                              <TableCell
                                sx={{ maxWidth: "200px" }}
                                className={styles.tableRow}
                              >
                                <Tooltip
                                  title={
                                    v?.organizationId?.name?.length > 17
                                      ? v?.organizationId?.name
                                      : "Not Available"
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <span className={styles.truncateText}>
                                    {v?.organizationId?.name || "Not Available"}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                sx={{ maxWidth: "160px" }}
                                className={styles.tableRow}
                              >
                                <Tooltip
                                  title={
                                    v?.campaignName?.length > 17
                                      ? v.campaignName
                                      : ""
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <span className={styles.truncateText}>
                                    {`${v?.campaignName?.charAt(0).toUpperCase() + v?.campaignName?.slice(1)}` ||
                                      "Not Available"}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell className={styles.tableRow}>
                                {v?.campaignOwnerId?.name?.length > 14 ? (
                                  <>
                                    <Tooltip
                                      title={v.campaignOwnerId.name}
                                      arrow
                                      placement="top"
                                    >
                                      <span className={styles.truncateText}>
                                        {v?.campaignOwnerId?.name
                                          ? v?.campaignOwnerId?.name.length > 14
                                            ? v?.campaignOwnerId?.name
                                                .charAt(0)
                                                .toUpperCase() +
                                              v?.campaignOwnerId?.name.slice(
                                                1,
                                                14,
                                              ) +
                                              "..."
                                            : v?.campaignOwnerId?.name
                                                .charAt(0)
                                                .toUpperCase() +
                                              v?.campaignOwnerId?.name.slice(1)
                                          : "Not Available"}
                                      </span>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <span>
                                    {v?.campaignOwnerId?.name
                                      .charAt(0)
                                      .toUpperCase() +
                                      v?.campaignOwnerId?.name.slice(1)}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className={styles.tableRow}>
                                <span>
                                  {`${
                                    v?.turnAroundTime == undefined
                                      ? "Not Available"
                                      : `${v?.turnAroundTime} Days`
                                  } `}
                                </span>
                              </TableCell>
                              <TableCell className={styles.tableRow}>
                                <Stack direction="row" spacing={1}>
                                  {/* Sirf pehla productType chip me dikhana */}
                                  {(Array.isArray(v.productType)
                                    ? v.productType
                                    : typeof v.productType === "string"
                                      ? v.productType.split(",")
                                      : []
                                  )
                                    .slice(0, 1)
                                    .map((s, i) => (
                                      <Chip
                                        key={i}
                                        label={s.trim()}
                                        sx={{
                                          backgroundColor: "#F1F3F4",
                                          color: "#3C4043",
                                          fontWeight: 500,
                                          borderRadius: "6px",
                                          height: "26px",
                                        }}
                                      />
                                    ))}

                                  {/* Agar 1 se jyada ho to +N tooltip ke andar vertical list dikhana */}
                                  {(Array.isArray(v.productType)
                                    ? v.productType
                                    : typeof v.productType === "string"
                                      ? v.productType.split(",")
                                      : []
                                  ).length > 1 && (
                                    <Tooltip
                                      arrow
                                      title={
                                        <div style={{ textAlign: "left" }}>
                                          {(Array.isArray(v.productType)
                                            ? v.productType
                                            : typeof v.productType === "string"
                                              ? v.productType.split(",")
                                              : []
                                          ).map((item, i) => (
                                            <div key={i}>
                                              {i + 1}. {item.trim()}
                                            </div>
                                          ))}
                                        </div>
                                      }
                                    >
                                      <Box
                                        sx={{
                                          fontSize: "13px",
                                          color: "#5f6368",
                                          fontWeight: 500,
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        +
                                        {(Array.isArray(v.productType)
                                          ? v.productType
                                          : typeof v.productType === "string"
                                            ? v.productType.split(",")
                                            : []
                                        ).length - 1}{" "}
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>

                              <TableCell className={styles.tableRow}>
                                {Array.isArray(v.associatedBrands) &&
                                v.associatedBrands.length > 0 ? (
                                  (() => {
                                    const brands = v.associatedBrands[0]
                                      .split(",")
                                      .map((b) => b.trim())
                                      .filter((b) => b);

                                    const first = brands[0];
                                    const rest = brands.slice(1);

                                    return (
                                      <Stack direction="row" spacing={0}>
                                        <span className="mx-2">{first}</span>

                                        {rest.length > 0 && (
                                          <Tooltip
                                            arrow
                                            title={
                                              <div
                                                style={{ textAlign: "left" }}
                                              >
                                                {brands.map((brand, i) => (
                                                  <div key={i}>
                                                    {i + 1}. {brand}
                                                  </div>
                                                ))}
                                              </div>
                                            }
                                          >
                                            <span
                                              style={{
                                                marginLeft: 0,
                                                cursor: "pointer",
                                                color: "#5f6368",
                                                fontWeight: 500,
                                              }}
                                            >
                                              +{rest.length} more
                                            </span>
                                          </Tooltip>
                                        )}
                                      </Stack>
                                    );
                                  })()
                                ) : (
                                  <span>Not Available</span>
                                )}
                              </TableCell>

                              {(localStorage.getItem("role") == "-1" ||
                                localStorage.getItem("role") == "1") && (
                                <>
                                  <TableCell
                                    className={styles.tableRow}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <KamDropdown
                                      setClientAddIndicationId={
                                        setClientAddIndicationId
                                      }
                                      clientAddIndicationId={
                                        clientAddIndicationId
                                      }
                                      kamdata={kamdata || []}
                                      enquiryData={v}
                                      getRfpList={getRfpList}
                                      initialSelected={
                                        v.keyAccountManagerId || []
                                      }
                                    />

                                    {/* <RfpModal
                                      getRfpList={getRfpList}
                                      rfpKamID={selectedRfpKamId}
                                      rfpID={selectedRfpId}
                                      kamdata={kamdata}
                                      open={rfpmodalOpen}
                                      onClose={handleCloseKamModal}
                                    /> */}
                                  </TableCell>
                                </>
                              )}

                              {localStorage.getItem("role") == "3" ? (
                                <></>
                              ) : localStorage.getItem("role") == "0" ? (
                                <>
                                  <TableCell
                                    className={styles.tableRow}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="flex justify-center">
                                      <Tooltip title="Click">
                                        <IconButton
                                          onClick={(e) =>
                                            handleIconOpen(e, v._id)
                                          }
                                        >
                                          <MoreVertIcon
                                            style={{
                                              color: "var(--primary-color)",
                                            }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                    <Menu
                                      anchorEl={anchorEl}
                                      open={
                                        Boolean(anchorEl) &&
                                        selectedRowId === v._id
                                      }
                                      onClose={handleIconClose}
                                      keepMounted
                                    >
                                      <MenuItem
                                        onClick={(e) => {
                                          handleViewRFP(v, "edit");
                                          setSelectedRow(v);
                                        }}
                                        disabled={isActionLoading}
                                      >
                                        <EditOutlined
                                          style={{ color: "#6b7280" }}
                                        />{" "}
                                        <p className="ml-2 font-semibold text-[#6b7280]">
                                          Edit
                                        </p>
                                      </MenuItem>

                                      <MenuItem
                                        onClick={() =>
                                          downloadExcelForInputLocations(
                                            v.campaignName,
                                            v.productType,
                                            v?.existingData,
                                            v?.regions,
                                            v.organizationId,
                                          )
                                        }
                                        // disabled={isActionLoading}
                                      >
                                        <DownloadIcon
                                          style={{ color: "#6b7280" }}
                                        />{" "}
                                        <p className="ml-2 font-semibold text-[#6b7280]">
                                          Download Template
                                        </p>
                                      </MenuItem>
                                      <MenuItem
                                        onClick={() => {
                                          setProductTypes(
                                            Array.isArray(v.productType)
                                              ? v.productType
                                              : [],
                                          );
                                          setSelectedRow(v);
                                          setOpenUploadModal(true);
                                          handleIconClose();
                                        }}
                                      >
                                        <DriveFolderUploadIcon
                                          style={{ color: "#6b7280" }}
                                        />
                                        <p className="ml-2 font-semibold text-[#6b7280]">
                                          Upload locations
                                        </p>
                                      </MenuItem>

                                      {localStorage.getItem("role") == "-1" && (
                                        <MenuItem
                                          onClick={(e) => handleDelete(e, v)}
                                        >
                                          <DeleteOutlineIcon
                                            style={{ color: "red" }}
                                          />{" "}
                                          <p className="ml-2 font-semibold text-[red]">
                                            Delete
                                          </p>
                                        </MenuItem>
                                      )}
                                    </Menu>
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell
                                    className={styles.tableRow}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="relative px-2">
                                      <div
                                        title={
                                          v.status === "Order Confirmed"
                                            ? "Order Confirmed"
                                            : "Confirmation Pending"
                                        }
                                        className="flex justify-center items-center gap-4"
                                      >
                                        {/* Toggle */}
                                        <label className="relative inline-flex items-center cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={
                                              v.status === "Order Confirmed"
                                            }
                                            onChange={(e) =>
                                              handleStatusChange(e, v)
                                            }
                                            disabled={
                                              v.status === "Order Confirmed"
                                            }
                                            className="sr-only peer"
                                          />

                                          <div
                                            className={`w-12 h-6 rounded-full transition-all duration-300
                                            ${v.status === "Order Confirmed" ? "bg-green-500 cursor-not-allowed" : "bg-gray-300"}
                                             peer-focus:ring-2 peer-focus:ring-blue-400
                                             after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                                             after:bg-white after:border after:rounded-full after:h-5 after:w-5
                                             after:transition-all
                                             ${v.status === "Order Confirmed" ? "after:translate-x-6" : ""}
                                           `}
                                          ></div>
                                        </label>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell
                                    className={styles.tableRow}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="flex justify-center">
                                      <Tooltip title="Click">
                                        <IconButton
                                          onClick={(e) =>
                                            handleIconOpen(e, v._id)
                                          }
                                        >
                                          <MoreVertIcon
                                            style={{
                                              color: "var(--primary-color)",
                                            }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                    <Menu
                                      anchorEl={anchorEl}
                                      open={
                                        Boolean(anchorEl) &&
                                        selectedRowId === v._id
                                      }
                                      onClose={handleIconClose}
                                      keepMounted
                                    >
                                      <MenuItem
                                        onClick={(e) => {
                                          handleViewRFP(v, "edit");
                                          setSelectedRow(v);
                                        }}
                                        disabled={isActionLoading}
                                      >
                                        <EditOutlined
                                          style={{ color: "#6b7280" }}
                                        />
                                        <p className="ml-2 font-semibold text-[#6b7280]">
                                          {`Edit`}
                                        </p>
                                      </MenuItem>
                                      <MenuItem
                                        onClick={(e) => {
                                          handleDuplicateRFP(v, "duplicate");
                                          setSelectedRow(v);
                                        }}
                                        disabled={isActionLoading}
                                      >
                                        <User_plus
                                          style={{ color: "#6b7280" }}
                                        />
                                        <p className="ml-2 font-semibold text-[#6b7280]">
                                          {`Duplicate`}
                                        </p>
                                      </MenuItem>
                                      <MenuItem
                                        onClick={() =>
                                          downloadExcelForInputLocations(
                                            v.campaignName,
                                            v.productType,
                                            v?.existingData,
                                            v?.regions,
                                            v.organizationId,
                                          )
                                        }
                                      >
                                        <DownloadIcon
                                          style={{ color: "#6b7280" }}
                                        />{" "}
                                        <p className="ml-2 font-semibold text-[#6b7280]">
                                          Download Template
                                        </p>
                                      </MenuItem>
                                      <MenuItem
                                        onClick={() => {
                                          setProductTypes(
                                            Array.isArray(v.productType)
                                              ? v.productType
                                              : [],
                                          );
                                          setSelectedRow(v);
                                          setOpenUploadModal(true);
                                          handleIconClose();
                                        }}
                                      >
                                        <DriveFolderUploadIcon
                                          style={{ color: "#6b7280" }}
                                        />
                                        <p className="ml-2 font-semibold text-[#6b7280]">
                                          Upload locations
                                        </p>
                                      </MenuItem>
                                      {hasReportService && (
                                        <MenuItem
                                          onClick={(e) => handleReports(e, v)}
                                          className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-md transition-all"
                                        >
                                          <DescriptionIcon
                                            style={{ color: "#6b7280" }}
                                          />

                                          <span
                                            className={`text-sm font-semibold px-2 py-1 rounded-md
        ${
          v?.reportsStatus === "latest"
            ? "bg-green-100 text-green-700"
            : v?.reportsStatus === "update"
              ? "bg-orange-100 text-orange-700"
              : "bg-blue-100 text-blue-700"
        }
      `}
                                          >
                                            {v?.reportsStatus === "latest"
                                              ? "Latest Reports"
                                              : v?.reportsStatus === "update"
                                                ? "Update Reports"
                                                : "Generate Reports"}
                                          </span>
                                        </MenuItem>
                                      )}{" "}
                                      <MenuItem
                                        onClick={(e) => {
                                          navigate(
                                            `/adminPortal/rfp/${v._id}/assign-delivery-center`,
                                          );
                                          handleIconClose();
                                        }}
                                      >
                                        {`Assign Delivery Center`}
                                      </MenuItem>
                                      {localStorage.getItem("role") == "-1" && (
                                        <MenuItem
                                          onClick={(e) => handleDelete(e, v)}
                                        >
                                          <DeleteOutlineIcon
                                            style={{ color: "red" }}
                                          />{" "}
                                          <p className="ml-2 font-semibold text-[red]">
                                            Delete
                                          </p>
                                        </MenuItem>
                                      )}
                                    </Menu>
                                  </TableCell>
                                  <TableCell
                                    className={styles.tableRow}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <Tooltip title="Services Settings">
                                          <IconButton
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenServicesDialog(v);
                                            }}
                                          >
                                            <Settings
                                              style={{
                                                color: "var(--primary-color)",
                                              }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </div>
                                    </div>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={headCells?.length}
                          sx={{ borderBottom: "none" }}
                        >
                          <div className="flex justify-center items-center h-64">
                            No Enquiry found
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Table>
                </TableContainer>
                <hr
                  className="mt-2 bg-gray-200 "
                  style={{ border: "0.5px solid var(--light-grey-color)" }}
                />

                <div className="flex items-center justify-between ">
                  <FormControl
                    variant="standard"
                    size="small"
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 1,
                      "& .MuiInputBase-root:before": { borderBottom: "none" },
                      "& .MuiInputBase-root:after": { borderBottom: "none" },
                      "& .MuiInputBase-root": { marginTop: 1 }, // 👈 override here
                    }}
                  >
                    <InputLabel
                      id="page-size-label"
                      sx={{
                        position: "static",
                        transform: "none",
                        fontSize: "14px",
                        marginRight: "8px",
                        marginTop: "2px",
                      }}
                    >
                      Rows
                    </InputLabel>

                    <Select
                      labelId="page-size-label"
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      variant="standard"
                      disableUnderline
                    >
                      {pageSizeOptions.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <GlobalSnackbar
                    snackbar={snackbar}
                    handleClose={handleSnackbarClose}
                  />

                  <Pagination
                    className="p-2 flex justify-center"
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#418cff", // Blue background for selected
                        color: "#fff", // White text for selected
                      },
                      "& .MuiPaginationItem-root.Mui-selected:hover": {
                        backgroundColor: "#3366cc", // Darker blue on hover
                      },
                      "& .MuiPaginationItem-root": {
                        "&:hover": {
                          backgroundColor: "#ddd", // Light gray for hovered items
                        },
                      },
                    }}
                  />
                </div>
              </>
            </Paper>
          </>
        ) : (
          <div className={styles.tableLoading}>
            <CircularProgress />
          </div>
        )}
      </Box>

      <ServicesSettingsDialog
        open={servicesDialogOpen}
        onClose={handleCloseServicesDialog}
        services={servicesDialogData.services}
        rfpId={servicesDialogData.rfpId}
        rfpSerialNumber={servicesDialogData.rfpSerialNumber}
        onServiceToggle={handleServiceToggle}
        isOldRFP={servicesDialogData.isOldRFP}
        setSnackbar={setSnackbar}
        missingServicesCount={servicesDialogData.missingServicesCount}
        totalServicesCount={servicesDialogData.totalServicesCount}
      />
      {isActionLoading ? (
        <CustomLoading />
      ) : (
        isSowModal && (
          <SowModal
            isProductDimensions={isProductDimensions}
            locationListUpdate={locationListUpdate}
            isCampaignId={isCampaignId}
            setSOWUpdateDetails={setSOWUpdateDetails}
            isSowModal={isSowModal}
            getSOWIds={getSOWIds}
            SOWUpdateDetails={SOWUpdateDetails}
            getRfpList={getRfpList}
            rfpData={selectedRow}
            setIsSowModal={setIsSowModal}
            setSnackbar={setSnackbar}
            sowReportRef={sowReportRef}
            deleteCheck={deleteCheck}

            // sowReport={sowReport}
          />
        )
      )}
      {isActionLoading ? (
        <CustomLoading />
      ) : (
        isTrashModalOpen && (
          <DeletedUsersRestore
            list={allDeletedEnquiries}
            type="ENQUIRY"
            openModal={isTrashModalOpen}
            loading={isTrashLoading}
            getAllDeletedUsers={getALLDeletedEnquiries}
            setIsTrashModalOpen={setIsTrashModalOpen}
            setTrashLoading={setTrashLoading}
            getAllList={getRfpList}
          />
        )
      )}

      {isActionLoading ? (
        <CustomLoading />
      ) : (
        isViewModal && (
          <SowViewModal
            isCampaignId={isCampaignId}
            isViewModal={isViewModal}
            setIsViewModal={setIsViewModal}
            setSnackbar={setSnackbar}
            rfpData={selectedRow}
            isProductDimensions={isProductDimensions}
            SOWUpdateDetails={SOWUpdateDetails}
            locationListUpdate={locationListUpdate}
          />
        )
      )}
      {openKamAssignModal && (
        <ClientMembersModal
          open={openKamAssignModal}
          getRfpList={getRfpList}
          selectedStatus={status}
          organizationId={isOrganizationId}
          onClose={() => setOpenKamAssignModal(false)}
          isEnquiryData={isEnquiryData}
          createCampaignFromRfp={createCampaignFromRfp}
          setSnackbar={setSnackbar}
          kamData={kamdata}
          setSelectedMembers={setSelectedMembers}
          selectedMembers={selectedMembers}
          snackbar={snackbar}
        />
      )}

      {/* {isSowReportOpen && (
        <SowPrintingReportModal
          selectedRow={selectedRow}
          open={isSowReportOpen}
          onClose={handleSowReportClose}
        />
      )} */}
      {isReportsModal && (
        <Modal
          open={isReportsModal}
          // onClose={handleCloseMachineBox}
        >
          <div
            className="h-[600px] w-full overflow-scroll flex justify-center items-start border border-1 border-blue-700 m-4
          "
          >
            <ReportsMainModal
              formData={formData}
              operation={operation}
              enquiryId={formData?._id}
              isRfpSerial={formData?.rfpSerialNumber}
              setFormData={setFormData}
              setRfpGenerate={setRfpGenerate}
              getRfpList={getRfpList}
              setReportsModal={setReportsModal}
              adminPortalView={adminPortalView}
            />
          </div>
        </Modal>
      )}
      {isSowReportOpen && (
        <SowReportDialog
          selectedRow={selectedRow}
          open={isSowReportOpen}
          onClose={handleSowReportClose}
        />
      )}
      <Dialog
        open={openUploadModal}
        onClose={() => !excelLoading && setOpenUploadModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Upload Location Data</DialogTitle>

        <DialogContent>
          <div className="flex justify-center mt-4">
            <div
              className="flex flex-col items-center justify-evenly border-2 border-dashed border-gray-300 
                   rounded-md p-2 max-w-40 h-40 text-center cursor-pointer 
                   hover:border-blue-500 transition"
              onClick={!excelLoading ? handleIconClick : undefined}
            >
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                <img src={XLS} alt="XLS" className="w-6 h-6" />
              </div>

              {!excelLoading ? (
                <div className="text-blue-600 font-medium text-xs">
                  <label className="cursor-pointer">Upload Location Data</label>

                  <input
                    type="file"
                    ref={locationFileInputRef}
                    accept=".xlsx"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setSelectedFile(file);
                    }}
                  />
                </div>
              ) : (
                <CircularProgress size={28} />
              )}

              <p className="text-xs text-black-600">(Max. File size: 2 MB)</p>
            </div>
          </div>
          {selectedFile && (
            <p className="mt-3 text-sm text-center text-gray-700">
              Selected file: <b>{selectedFile.name}</b>
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <GlobalButton
            size="medium"
            variant="secondary"
            onClick={() => {
              setOpenUploadModal(false);
              setSelectedFile(null);
            }}
            disabled={excelLoading}
            style={{ width: "auto" }}
          >
            {`Cancel`}
          </GlobalButton>

          <GlobalButton
            variant="primary"
            size="medium"
            disabled={!selectedFile || excelLoading}
            isLoading={excelLoading}
            onClick={handleUploadLocations}
            style={{ width: "auto" }}
          >
            {`Upload`}
          </GlobalButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
